import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/layouts/PageContainer";
import { CreditCard, ChevronLeft, AlertCircle, User, Plus, X } from "lucide-react";

const nativeSelect =
  "h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground disabled:opacity-50";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function SeatManagement() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [school, setSchool] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddSeats, setShowAddSeats] = useState(false);
  const [addCount, setAddCount] = useState(1);
  const [adding, setAdding] = useState(false);

  const [renewalCount, setRenewalCount] = useState("");
  const [savingRenewal, setSavingRenewal] = useState(false);

  const [actionInProgress, setActionInProgress] = useState(null);

  const [purchaseCount, setPurchaseCount] = useState(5);
  const [checkingOut, setCheckingOut] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, membersRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, { headers }),
      ]);
      if (!schoolRes.ok) {
        const body = await schoolRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load school");
      }
      const schoolData = await schoolRes.json();
      setSchool(schoolData);
      setRenewalCount(
        schoolData.scheduledRenewalSeats != null
          ? String(schoolData.scheduledRenewalSeats)
          : String(schoolData.seats?.length ?? "")
      );
      if (membersRes.ok) setMembers(await membersRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const assignedUserIds = new Set(
    (school?.seats ?? [])
      .map((s) => s.currentAssignee)
      .filter(Boolean)
      .map(String)
  );

  const handleAssign = async (idx, userId) => {
    const key = userId ? `assign-${idx}` : `unassign-${idx}`;
    setActionInProgress(key);
    try {
      const token = await getToken();
      const method = userId ? "PUT" : "DELETE";
      const opts = {
        method,
        headers: { Authorization: `Bearer ${token}` },
      };
      if (userId) {
        opts.headers["Content-Type"] = "application/json";
        opts.body = JSON.stringify({ userId });
      }
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/seats/${idx}/assign`,
        opts
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update seat");
      }
      const data = await res.json();
      setSchool((prev) => ({ ...prev, seats: data.seats }));
      toast.success(userId ? "Seat assigned." : "Seat unassigned.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleNextYear = async (idx, userId) => {
    setActionInProgress(`nextyear-${idx}`);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/seats/${idx}/next-year`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update next-year assignment");
      }
      const data = await res.json();
      setSchool((prev) => ({ ...prev, seats: data.seats }));
      toast.success("Next-year assignment saved.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAddSeats = async (e) => {
    e.preventDefault();
    const count = Number(addCount);
    if (!count || count < 1) return;
    setAdding(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/seats/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ count }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to add seats");
      }
      const data = await res.json();
      setSchool((prev) => ({ ...prev, seats: data.seats }));
      setAddCount(1);
      setShowAddSeats(false);
      toast.success(`${count} seat${count !== 1 ? "s" : ""} added.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleSaveRenewal = async (e) => {
    e.preventDefault();
    const count = Number(renewalCount);
    if (isNaN(count) || count < 0) return;
    if (
      count === 0 &&
      !confirm(
        "Setting renewal seats to 0 will cancel your subscription at the end of the billing period. Continue?"
      )
    )
      return;
    setSavingRenewal(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/renewal-seats`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ count }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to save renewal count");
      }
      const data = await res.json();
      setSchool((prev) => ({
        ...prev,
        scheduledRenewalSeats: data.school?.scheduledRenewalSeats ?? count,
      }));
      toast.success(
        count === 0
          ? "Subscription will be cancelled on renewal."
          : "Renewal seat count saved."
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingRenewal(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    const count = Number(purchaseCount);
    if (!count || count < 1) return;
    setCheckingOut(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ seatCount: count }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create checkout session");
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      toast.error(err.message);
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  const seats = school?.seats ?? [];
  const hasSubscription =
    !!school?.stripeSubscriptionId && school?.subscriptionStatus !== "none";
  const status = school?.subscriptionStatus;

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <CreditCard size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">
            Seat Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Assign seats and manage your school&apos;s billing.
          </p>
        </div>
      </div>

      {!hasSubscription ? (
        <div className="max-w-[480px]">
          <div className="bg-card border border-border rounded-2xl px-6 py-6">
            <h2 className="text-sm font-semibold text-foreground mb-1">
              Purchase School Seats
            </h2>
            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
              Each seat gives one teacher access to the School plan - 120 reports and 200
              AI generations per year. Billed annually through Stripe.
            </p>
            <form onSubmit={handlePurchase} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Number of Seats</Label>
                <Input
                  type="number"
                  min={1}
                  value={purchaseCount}
                  onChange={(e) => setPurchaseCount(e.target.value)}
                  className="h-10 max-w-[160px]"
                  required
                />
              </div>
              <Button type="submit" disabled={checkingOut || !purchaseCount}>
                {checkingOut ? "Redirecting to Stripe..." : "Purchase Seats"}
              </Button>
            </form>
          </div>
        </div>
      ) : (
        <>
          {/* Subscription status bar */}
          <div className="bg-card border border-border rounded-xl px-5 py-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Status</p>
              <span
                className={`inline-block mt-0.5 text-sm font-semibold ${
                  status === "active"
                    ? "text-green-600 dark:text-green-400"
                    : status === "past_due"
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-muted-foreground"
                }`}
              >
                {status === "active"
                  ? "Active"
                  : status === "past_due"
                  ? "Past Due"
                  : status === "cancelled"
                  ? "Cancelled"
                  : status ?? "-"}
              </span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Seats</p>
              <p className="text-sm font-semibold text-foreground mt-0.5">
                {seats.length}
              </p>
            </div>
            {school?.currentPeriodEnd && (
              <div>
                <p className="text-xs text-muted-foreground font-medium">Renews</p>
                <p className="text-sm font-semibold text-foreground mt-0.5">
                  {formatDate(school.currentPeriodEnd)}
                </p>
              </div>
            )}
            {school?.scheduledRenewalSeats === 0 && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-destructive/10 text-destructive">
                Cancels on renewal
              </span>
            )}
          </div>

          {/* Seat grid */}
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground">
              Seats ({seats.length})
            </h2>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-8"
              onClick={() => setShowAddSeats((v) => !v)}
            >
              <Plus size={13} />
              Add Seats
            </Button>
          </div>

          {showAddSeats && (
            <form
              onSubmit={handleAddSeats}
              className="bg-card border border-border rounded-xl px-4 py-4 mb-3 flex items-end gap-3 flex-wrap"
            >
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">How many to add?</Label>
                <Input
                  type="number"
                  min={1}
                  value={addCount}
                  onChange={(e) => setAddCount(e.target.value)}
                  className="h-9 w-24"
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={adding} className="h-9">
                  {adding ? "Adding..." : "Add"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0"
                  onClick={() => setShowAddSeats(false)}
                >
                  <X size={14} />
                </Button>
              </div>
            </form>
          )}

          {seats.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center rounded-2xl border border-dashed border-border bg-muted/20 mb-6">
              <CreditCard size={28} className="text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No seats yet</p>
            </div>
          ) : (
            <div className="space-y-2 mb-8">
              {seats.map((seat, idx) => {
                const assignBusy =
                  actionInProgress === `assign-${idx}` ||
                  actionInProgress === `unassign-${idx}`;
                const nextBusy = actionInProgress === `nextyear-${idx}`;
                const assignedId = seat.currentAssignee
                  ? String(seat.currentAssignee)
                  : "";
                const nextYearId = seat.nextYearAssignee
                  ? String(seat.nextYearAssignee)
                  : "";

                const availableForAssign = members.filter((m) => {
                  const mid = String(m.userId);
                  return !assignedUserIds.has(mid) || mid === assignedId;
                });

                return (
                  <div
                    key={idx}
                    className="bg-card border border-border rounded-xl px-5 py-4"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <User size={14} className="text-muted-foreground shrink-0" />
                      <span className="text-sm font-semibold text-foreground">
                        Seat {idx + 1}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 max-[560px]:grid-cols-1">
                      <div className="space-y-1.5">
                        <p className="text-xs text-muted-foreground font-medium">
                          This Year
                        </p>
                        <select
                          className={nativeSelect}
                          value={assignedId}
                          disabled={assignBusy}
                          onChange={(e) => handleAssign(idx, e.target.value)}
                        >
                          <option value="">- Unassigned -</option>
                          {availableForAssign.map((m) => (
                            <option key={String(m.userId)} value={String(m.userId)}>
                              {m.name || m.email}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-xs text-muted-foreground font-medium">
                          Next Year{" "}
                          <span className="font-normal text-muted-foreground/60">
                            (pre-assign)
                          </span>
                        </p>
                        <select
                          className={nativeSelect}
                          value={nextYearId}
                          disabled={nextBusy}
                          onChange={(e) => handleNextYear(idx, e.target.value)}
                        >
                          <option value="">- Not pre-assigned -</option>
                          {members.map((m) => (
                            <option key={String(m.userId)} value={String(m.userId)}>
                              {m.name || m.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Renewal settings */}
          <div className="bg-card border border-border rounded-2xl px-6 py-5 max-w-[480px]">
            <h2 className="text-sm font-semibold text-foreground mb-1">
              Renewal Settings
            </h2>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Set how many seats to purchase on the next renewal. Set to{" "}
              <span className="font-medium">0</span> to cancel the subscription when the
              current period ends.
            </p>
            <form
              onSubmit={handleSaveRenewal}
              className="flex items-end gap-3 flex-wrap"
            >
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Seats at Renewal</Label>
                <Input
                  type="number"
                  min={0}
                  value={renewalCount}
                  onChange={(e) => setRenewalCount(e.target.value)}
                  className="h-9 w-24"
                  required
                />
              </div>
              <Button
                type="submit"
                variant="outline"
                size="sm"
                disabled={savingRenewal}
                className="h-9"
              >
                {savingRenewal ? "Saving..." : "Save"}
              </Button>
            </form>
            {renewalCount === "0" && (
              <p className="text-xs text-destructive mt-2">
                Subscription will be cancelled at the end of the current period.
              </p>
            )}
          </div>
        </>
      )}
    </PageContainer>
  );
}
